<template>
  <div class="form__field-wrapper">
    <v-progress-circular
      v-if="isLoading && showSearchAddress"
      class="form__field-loading"
      color="primary"
      size="25"
      width="3"
      indeterminate
      rounded
    />

    <div v-if="showSearchAddress" class="realtySearch__search-wrapper">
      <v-autocomplete
        v-model="model"
        @change="selectItem"
        :items="items"
        :rules="rules"
        :search-input.sync="search"
        :label="$t('appointment.formCreate.labels.searchRealty')"
        item-value="id"
        class="realtySearch"
        item-text="description"
        append-icon="mdi-magnify"
        hide-details="auto"
        auto-select-first
        return-object
        hide-no-data
        outlined
      >
        <template v-slot:item="data">
          <v-row class="realtySearch__autocomplete" align="center" no-gutters>
            <v-col cols="2">
              <v-chip x-small>
                <span class="d-inline-block text-truncate">
                  {{ data.item.code }}
                </span>
              </v-chip>
            </v-col>
            <v-col cols="10">
              <small class="d-block text-truncate">{{
                data.item.address
              }}</small>
            </v-col>
          </v-row>
        </template>
      </v-autocomplete>

      <v-btn
        @click="openRealtyCreateModal"
        class="realtySearch__create-realty"
        color="primary"
        small
        text
      >
        <v-icon small>mdi-home-plus-outline</v-icon>
        <p class="caption">
          {{ $t('appointment.formCreate.labels.createRealty') }}
        </p>
      </v-btn>
    </div>

    <v-card
      v-if="realties.length >= 1"
      class="realtySearch__selected-wrapper"
      elevation="0"
      outlined
      flat
    >
      <h6 class="realtySearch__title text-uppercase grey--text">
        {{
          realties.length === 1
            ? $t('appointment.label.selectedRealty')
            : $t('appointment.label.selectedRealties')
        }}
      </h6>

      <v-hover
        v-slot="{ hover }"
        v-for="realty in realties_complete"
        :key="realty.id"
      >
        <v-row
          :class="{ 'grey lighten-4': hover }"
          class="realtySearch__selected-line"
          align="center"
          justify="end"
          no-gutters
        >
          <v-col class="body-2 grey--text text--darken-2 py-0" cols="7">
            <v-row align="center">
              <v-col cols="3">
                <v-chip
                  class="mr-3 grey--text text--darken-2"
                  :class="{ white: hover }"
                  small
                >
                  <span class="d-inline-block text-truncate">
                    {{ realty.code }}
                  </span>
                </v-chip>
              </v-col>
              <v-col class="text-truncate" cols="9">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      {{ realty.address }}
                    </span>
                  </template>
                  <span>{{ realty.address }}</span>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-col>

          <v-col class="d-flex justify-end" cols="5">
            <RealtySearchDetails
              @updateSelectedItem="updateSelectedItem"
              :realty="realty"
            />

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  @click="removeItem(realty.id)"
                  v-bind="attrs"
                  v-on="on"
                  icon
                >
                  <v-icon small>
                    mdi-close
                  </v-icon>
                </v-btn>
              </template>
              {{ $t('appointment.formCreate.labels.removeRealty') }}
            </v-tooltip>
          </v-col>

          <div
            v-if="checkDetailsData(realty)"
            class="form__no-meters caption lighten-4 red--text text--darken-2"
          >
            <!--
              <p v-if="!realty.type" class="mb-0">
                {{ $t('realty.warnings.withoutType') }}
              </p>
            -->
            <p v-if="!realty.private_area_meters" class="mb-0">
              {{ $t('realty.warnings.withoutPrivateArea') }}
            </p>
            <p v-if="!realty.available" class="mb-0">
              {{ $t('realty.warnings.realtyNotAvailable') }}
            </p>
            <p v-if="realty.furnished === null" class="mb-0">
              {{ $t('realty.warnings.furnishedNull') }}
            </p>
            <p v-if="realty.trousseau === null" class="mb-0">
              {{ $t('realty.warnings.trousseauNull') }}
            </p>
            <p v-if="realty.painting_status === null" class="mb-0">
              {{ $t('realty.warnings.paintingNull') }}
            </p>
          </div>
        </v-row>
      </v-hover>
    </v-card>

    <v-progress-linear v-if="isAddingRealty" indeterminate absolute rounded />

    <p
      v-if="search && !isLoading && searchResults.length <= 0"
      class="text-center red--text body-2 mb-0"
    >
      {{ $t('appointment.formCreate.labels.noRealty') }}.

      <v-btn @click="openRealtyCreateModal" class="pa-0" color="primary" text>
        <span class="body-2">
          {{ $t('appointment.formCreate.labels.createRealty') }}
        </span>
      </v-btn>
    </p>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'

import RealtySearchDetails from './RealtySearchDetails'

export default {
  name: 'RealtySearch',

  components: {
    RealtySearchDetails,
  },

  props: {
    rules: {
      type: Array,
    },

    single: {
      type: Boolean,
      default: false,
    },

    applyNewRealty: {
      type: Boolean,
      required: false,
      default: false,
    }
  },

  data() {
    return {
      model: null,
      search: null,
      realties: [],
      realties_complete: [],
      searchResults: [],
      _timerId: null,
      isLoading: false,
      isAddingRealty: false,
    }
  },

  watch: {
    search(term) {
      this.isLoading = true
      clearTimeout(this._timerId)

      this._timerId = setTimeout(() => {
        if (!term) {
          this.items = []
          this.searchResults = []
          this.isLoading = false

          return
        }

        this.searchItems(term)
      }, 1000)
    },

    appointmentFields(appointment) {
      if (!appointment?.type) {
        this.model = null
        this.search = null
        this.items = []
        this.searchResults = []
        this.realties_complete = []
      }
    },

    applyNewRealty(status) {
      status && this.applyNewRealtyCreateByInnerModal()
    },
  },

  computed: {
    ...mapGetters([
      'realty',
      'branches',
      'appointmentFields',
      'appointmentAmount',
      'createdRealtyId',
    ]),

    items: {
      get: function() {
        return this.searchResults.map((realty) =>
          this.parseSearchResultData(realty)
        )
      },

      set: function() {
        this.searchResults = []
      },
    },

    showSearchAddress() {
      return !(this.single && this.realties.length >= 1)
    },
  },

  methods: {
    ...mapActions([
      'searchRealties',
      'fetchRealtyDetails',
      'appointmentAddresses',
      'getAppointmentAmount',
    ]),

    ...mapMutations(['setRealty', 'setAppointmentFields', 'setAppointmentAddresses']),

    checkDetailsData(realty) {
      // !realty.type ||
      if (
        !realty.available ||
        !realty.private_area_meters ||
        realty.painting_status === null ||
        realty.furnished === null ||
        realty.trousseau === null
      ) {
        return true
      }
    },

    selectItem({ id }) {
      this.searchResults = []
      this.isAddingRealty = true

      this.fetchRealtyDetails(id)
        .then(() => {
          // check if realty exists
          // if true, remove from array and add the updated realty
          const realtyIndex = this.realties.findIndex((realty) => {
            return realty === id
          })

          if (realtyIndex >= 0) {
            this.realties.splice(realtyIndex, 1)
            this.realties_complete.splice(realtyIndex, 1)
          }
        })
        .then(() => {
          const realty = {
            id: this.realty.id,
            code: this.realty.code,
            address: this.realty.address,
            description: this.realty.description,
            latitude: this.realty.latitude,
            longitude: this.realty.longitude,
            square_meters: this.realty.square_meters,
            private_area_meters: this.realty.private_area_meters,
            furnished: this.realty.furnished,
            semi_furnished: this.realty.semi_furnished,
            painting_status: this.realty.painting_status,
            trousseau: this.realty.trousseau,
            available: this.realty.available,
            type: this.realty.realty_type,
          }

          this.realties.push(realty.id)
          this.realties_complete.push(realty)
        })
        .then(() => {
          this.setAppointmentFields({ realties: this.realties })
        })
        .then(() => {
          this.setAppointmentFields({ realties_complete: this.realties_complete })
        })
        .then(() => {
          // key address data
          const id = localStorage.getItem('users_branch_id')
          const address = localStorage.getItem('users_branch_address')

          this.setAppointmentFields({
            key_location_address: address,
            key_location_options: {
              id,
              key_picker: 'HOST',
              key_location: 'REAL_ESTATE',
              address: null,
            },
          })
        })
        .then(() => {
          // meeting address data
          const id = this.appointmentFields?.realties[0]
          const address = this.realties_complete[0]?.address
          const { meeting_location_options } = this.appointmentFields

          this.setAppointmentFields({
            meeting_location_options: {
              ...meeting_location_options,
              id,
              meeting_location: 'REALTY',
            },
            meeting_location_address: address,
            realties_complete: this.realties_complete,
          })
        })
        .then(() => {
          this.parseAddresses()
        })
        .then(() => {
          if (this.appointmentAmount !== null) {
            this.getAppointmentAmount()
          }
        })
        .then(() => this.setRealty({}))
        .finally(() => {
          this.isAddingRealty = false
        })
    },

    updateSelectedItem(id) {
      this.selectItem({ id })
    },

    removeItem(selectedId) {
      // used by form -------------------------------------------------------
      const realtyIndex = this.realties.indexOf(selectedId)

      this.realties.splice(realtyIndex, 1)
      this.realties_complete.splice(realtyIndex, 1)

      this.model = this.realties
      this.setAppointmentFields({ realties: this.realties })
      this.setAppointmentFields({ realties_complete: this.realties_complete })

      if (this.appointmentAmount !== null) this.getAppointmentAmount()
    },

    searchItems(term) {
      const realEstateId = this.appointmentFields.real_estate_id
      this.searchRealties({ searchText: term, realEstateId })
        .then(({ data }) => {
          this.searchResults = data.filter(this.isNotSelected)
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    isNotSelected(realty) {
      return !this.realties.some((id) => realty.id === id)
    },

    parseSearchResultData(realty) {
      return {
        id: realty.id,
        code: realty.code,
        address: realty.address,
        description: realty.code + ' - ' + realty.address,
      }
    },

    parseAddresses() {
      const branchGroup = [
        { header: this.$t('appointment.formCreate.labels.branches') },
      ]

      const realtyGroup = [
        { header: this.$t('appointment.formCreate.labels.realties') },
      ]

      const divider = [{ divider: true }]

      const branches = this.branches.map((branch) => {
        return { ...branch, location: 'REAL_ESTATE' }
      })

      const realties = this.realties_complete.map((realty) => {
        return { ...realty, location: 'REALTY' }
      })

      const addresses = branchGroup.concat(
        branches,
        divider,
        realtyGroup,
        realties
      )

      this.setAppointmentAddresses(addresses)
    },

    openRealtyCreateModal() {
      this.$emit('openRealtyCreateModal')
    },

    applyNewRealtyCreateByInnerModal() {
      if (!this.createdRealtyId) return

      this.model = { id: this.createdRealtyId }
      this.selectItem({ id: this.createdRealtyId })
    },

    resetValue() {
      this.model = null
      this.search = null
      this.items = []
      this.realties = []
      this.searchResults = []
      this.realties_complete = []
      this.setAppointmentAddresses([])
    },
  },
}
</script>
